import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import { en } from '../locales/en';
import { de } from '../locales/de';

export type Language = 'en' | 'de';

export const SUPPORTED_LANGUAGES: { code: Language; label: string }[] = [
  { code: 'en', label: 'English' },
  { code: 'de', label: 'Deutsch' },
];

export const DEFAULT_LANGUAGE: Language = 'en';

type TranslationDict = Record<string, unknown>;

const translations: Record<Language, TranslationDict> = { en, de };

function getNestedValue(obj: TranslationDict, path: string): string {
  const keys = path.split('.');
  let current: unknown = obj;
  for (const key of keys) {
    if (current === null || current === undefined || typeof current !== 'object') {
      return path;
    }
    current = (current as Record<string, unknown>)[key];
  }
  return typeof current === 'string' ? current : path;
}

interface I18nContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string, varsOrFallback?: Record<string, string> | string) => string;
}

const I18nContext = createContext<I18nContextType | undefined>(undefined);

export function I18nProvider({ children }: { children: React.ReactNode }) {
  const [language, setLanguageState] = useState<Language>(() => {
    const stored = localStorage.getItem('agentic_seo_language');
    return (stored === 'en' || stored === 'de') ? stored : DEFAULT_LANGUAGE;
  });

  const setLanguage = useCallback((lang: Language) => {
    setLanguageState(lang);
    localStorage.setItem('agentic_seo_language', lang);
  }, []);

  const t = useCallback((key: string, varsOrFallback?: Record<string, string> | string): string => {
    let value = getNestedValue(translations[language], key);
    // If the key wasn't found (returns the key itself), use fallback if provided
    if (value === key && typeof varsOrFallback === 'string') {
      return varsOrFallback;
    }
    if (varsOrFallback && typeof varsOrFallback === 'object') {
      Object.entries(varsOrFallback).forEach(([k, v]) => {
        value = value.replace(new RegExp(`\\{\\{${k}\\}\\}`, 'g'), v);
      });
    }
    return value;
  }, [language]);

  const contextValue = useMemo(() => ({
    language,
    setLanguage,
    t,
  }), [language, setLanguage, t]);

  return React.createElement(
    I18nContext.Provider,
    { value: contextValue },
    children
  );
}

export function useTranslation() {
  const context = useContext(I18nContext);
  if (context === undefined) {
    throw new Error('useTranslation must be used within an I18nProvider');
  }
  return context;
}
